/**
 * LoanShip MCP sign-in (docs/LOANSHIP-MCP.md). When an AI app connects, Pass's server-rendered
 * consent page (`/oauth/consent`) sends a signed-out or not-yet-confirmed browser here with
 * `?oauth_return=/oauth/consent?request=...`. The app remembers it for this tab, runs the normal
 * sign-in and passkey or Duo step, then goes back to the consent page. Only that one path shape is
 * accepted, so this can never redirect anywhere else.
 */
const KEY = "pass.oauth_return";
const CONSENT = /^\/oauth\/consent\?request=[A-Za-z0-9_-]{40,64}$/;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function safeOAuthReturn(value: string | null | undefined): string | null {
  return value && CONSENT.test(value) ? value : null;
}

/** Stores a valid `oauth_return` from the query string (Duo's callback lands on "/" without it). */
export function rememberOAuthReturn(search: string, store: Store | null): void {
  const value = safeOAuthReturn(new URLSearchParams(search).get("oauth_return"));
  if (!value || !store) return;
  try {
    store.setItem(KEY, value);
  } catch {
    // Storage blocked: the person starts the connection again from the AI app.
  }
}

/** The remembered consent page, once; null when there is none. */
export function takeOAuthReturn(store: Store | null): string | null {
  if (!store) return null;
  try {
    const value = safeOAuthReturn(store.getItem(KEY));
    store.removeItem(KEY);
    return value;
  } catch {
    return null;
  }
}

export function browserSession(): Store | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
